import { v4 as uuidv4 } from 'uuid';
import { ValidationError } from '../utils/errors.js';
import { validateBooking } from '../utils/validators.js';
import { calculateDuration } from '../utils/calculators.js';

/**
 * Represents a booking reservation
 */
export class Booking {
  /**
   * Create a new Booking
   * @param {Object} data - Booking configuration
   * @param {string} [data._id] - Unique identifier (auto-generated if not provided)
   * @param {string} data.memberId - Member GUID making the booking
   * @param {string} data.rinkId - Reference to the booked rink
   * @param {string} data.bookingTypeId - Reference to the booking type
   * @param {Date} data.date - Booking date
   * @param {Date} data.start - Booking start time
   * @param {Date} data.end - Booking end time
   * @param {string} [data.status='pending'] - Booking status
   * @param {Array<string>} [data.participants] - Array of member IDs
   * @param {string} [data.notes] - Additional booking notes
   * @param {Date} [data.createdDate] - Timestamp of booking creation
   * @param {Date} [data.updatedDate] - Timestamp of last update
   */
  constructor(data) {
    if (!data || typeof data !== 'object') {
      throw new ValidationError('Booking data is required');
    }

    // Validate required booking data
    validateBooking(data);

    this._id = data._id || uuidv4();
    this.memberId = data.memberId;
    this.rinkId = data.rinkId;
    this.bookingTypeId = data.bookingTypeId;
    this.date = new Date(data.date);
    this.start = new Date(data.start);
    this.end = new Date(data.end);
    this.status = data.status || 'pending';
    this.participants = Array.isArray(data.participants) ? [...data.participants] : [];
    this.notes = data.notes || '';
    this.createdDate = data.createdDate ? new Date(data.createdDate) : new Date();
    this.updatedDate = data.updatedDate ? new Date(data.updatedDate) : new Date();

    // Ensure member is in participants list
    if (!this.participants.includes(this.memberId)) {
      this.participants.unshift(this.memberId);
    }

    // Validate date consistency
    const bookingDay = this.date.toDateString();
    const startDay = this.start.toDateString();
    const endDay = this.end.toDateString();

    if (bookingDay !== startDay || bookingDay !== endDay) {
      throw new ValidationError(
        'Start and end times must be on the same date as the booking date',
        'dateConsistency',
        { date: this.date, start: this.start, end: this.end }
      );
    }
  }

  /**
   * Update booking status
   * @param {string} newStatus - New status to set
   * @param {string} memberId - Member performing the update
   * @returns {Booking} This instance for chaining
   */
  updateStatus(newStatus, memberId = null) {
    const validStatuses = ['pending', 'confirmed', 'cancelled', 'completed'];
    
    if (!validStatuses.includes(newStatus)) {
      throw new ValidationError('Invalid booking status', 'status', newStatus);
    }

    // Validate status transitions
    const validTransitions = {
      pending: ['confirmed', 'cancelled'],
      confirmed: ['cancelled', 'completed'],
      cancelled: [], // Cannot transition from cancelled
      completed: [] // Cannot transition from completed
    };

    if (!validTransitions[this.status].includes(newStatus)) {
      throw new ValidationError(
        `Invalid status transition from ${this.status} to ${newStatus}`,
        'statusTransition',
        { from: this.status, to: newStatus }
      );
    }

    this.status = newStatus;
    this.updatedDate = new Date();

    return this;
  }

  /**
   * Add participant to booking
   * @param {string} memberId - Member ID to add
   * @returns {Booking} This instance for chaining
   */
  addParticipant(memberId) {
    if (!memberId || typeof memberId !== 'string') {
      throw new ValidationError('Valid member ID is required', 'memberId', memberId);
    }

    if (!this.participants.includes(memberId)) {
      this.participants.push(memberId);
      this.updatedDate = new Date();
    }

    return this;
  }

  /**
   * Remove participant from booking
   * @param {string} memberId - Member ID to remove
   * @returns {Booking} This instance for chaining
   */
  removeParticipant(memberId) {
    if (!memberId || typeof memberId !== 'string') {
      throw new ValidationError('Valid member ID is required', 'memberId', memberId);
    }

    // Cannot remove the booking owner
    if (memberId === this.memberId) {
      throw new ValidationError('Cannot remove booking owner from participants', 'memberId', memberId);
    }

    const index = this.participants.indexOf(memberId);
    if (index > -1) {
      this.participants.splice(index, 1);
      this.updatedDate = new Date();
    }

    return this;
  }

  /**
   * Get booking duration in minutes
   * @returns {number} Duration in minutes
   */
  getDuration() {
    return calculateDuration(this.start, this.end);
  }

  /**
   * Check if booking is currently active
   * @returns {boolean} True if booking is currently active
   */
  isActive() {
    if (this.status !== 'confirmed') {
      return false;
    }

    const now = new Date();
    return now >= this.start && now <= this.end;
  }

  /**
   * Check if booking is in the past
   * @returns {boolean} True if booking is in the past
   */
  isPast() {
    const now = new Date();
    return this.end < now;
  }

  /**
   * Check if booking is in the future
   * @returns {boolean} True if booking is in the future
   */
  isFuture() {
    const now = new Date();
    return this.start > now;
  }

  /**
   * Check if booking can be modified
   * @returns {boolean} True if booking can be modified
   */
  canModify() {
    return this.status === 'pending' || this.status === 'confirmed';
  }

  /**
   * Check if booking can be cancelled
   * @returns {boolean} True if booking can be cancelled
   */
  canCancel() {
    return (this.status === 'pending' || this.status === 'confirmed') && !this.isPast();
  }

  /**
   * Update booking details
   * @param {Object} updates - Properties to update
   * @param {string} memberId - Member performing the update
   * @returns {Booking} This instance for chaining
   */
  update(updates, memberId = null) {
    if (!updates || typeof updates !== 'object') {
      throw new ValidationError('Updates object is required');
    }

    if (!this.canModify()) {
      throw new ValidationError(`Cannot modify booking with status: ${this.status}`, 'status', this.status);
    }

    const allowedFields = ['date', 'start', 'end', 'bookingTypeId', 'participants', 'notes'];
    const updateKeys = Object.keys(updates);

    for (const key of updateKeys) {
      if (!allowedFields.includes(key)) {
        throw new ValidationError(`Field '${key}' cannot be updated`, key, updates[key]);
      }
    }

    // Validate time updates
    const newDate = updates.date ? new Date(updates.date) : this.date;
    const newStart = updates.start ? new Date(updates.start) : this.start;
    const newEnd = updates.end ? new Date(updates.end) : this.end;

    if (newStart >= newEnd) {
      throw new ValidationError('End time must be after start time', 'time', {
        start: newStart,
        end: newEnd
      });
    }

    // Apply updates
    if (updates.date !== undefined) {
      this.date = newDate;
    }
    if (updates.start !== undefined) {
      this.start = newStart;
    }
    if (updates.end !== undefined) {
      this.end = newEnd;
    }
    if (updates.bookingTypeId !== undefined) {
      this.bookingTypeId = updates.bookingTypeId;
    }
    if (updates.participants !== undefined) {
      if (!Array.isArray(updates.participants)) {
        throw new ValidationError('Participants must be an array', 'participants', updates.participants);
      }
      this.participants = [...updates.participants];
      // Ensure booking owner is in participants
      if (!this.participants.includes(this.memberId)) {
        this.participants.unshift(this.memberId);
      }
    }
    if (updates.notes !== undefined) {
      this.notes = updates.notes || '';
    }

    this.updatedDate = new Date();
    return this;
  }

  /**
   * Get booking type reference (requires BookingType instances to be provided)
   * @param {Array<BookingType>} bookingTypes - Available booking types
   * @returns {BookingType|null} BookingType instance or null if not found
   */
  getBookingType(bookingTypes) {
    if (!Array.isArray(bookingTypes)) {
      return null;
    }

    return bookingTypes.find(type => type._id === this.bookingTypeId) || null;
  }

  /**
   * Check if booking overlaps with another booking
   * @param {Booking} otherBooking - Other booking to check
   * @returns {boolean} True if bookings overlap
   */
  overlapsWith(otherBooking) {
    if (!otherBooking || !(otherBooking instanceof Booking)) {
      return false;
    }

    // Different rinks don't overlap
    if (this.rinkId !== otherBooking.rinkId) {
      return false;
    }

    // Different dates don't overlap
    if (this.date.toDateString() !== otherBooking.date.toDateString()) {
      return false;
    }

    // Check time overlap
    return this.start < otherBooking.end && this.end > otherBooking.start;
  }

  /**
   * Convert to JSON string for persistence
   * @returns {string} JSON string representation
   */
  toJSON() {
    const data = {
      _id: this._id,
      memberId: this.memberId,
      rinkId: this.rinkId,
      bookingTypeId: this.bookingTypeId,
      date: this.date.toISOString().split('T')[0], // Date only
      start: this.start.toISOString(),
      end: this.end.toISOString(),
      status: this.status,
      participants: [...this.participants],
      notes: this.notes,
      createdDate: this.createdDate.toISOString(),
      updatedDate: this.updatedDate.toISOString()
    };
    return JSON.stringify(data);
  }

  /**
   * Create Booking instance from JSON string
   * @param {string} jsonString - JSON string data
   * @returns {Booking} New Booking instance
   */
  static fromJSON(jsonString) {
    if (!jsonString || typeof jsonString !== 'string') {
      throw new ValidationError('Valid JSON string is required');
    }

    let jsonData;
    try {
      jsonData = JSON.parse(jsonString);
    } catch (error) {
      throw new ValidationError('Invalid JSON string format');
    }

    return new Booking({
      _id: jsonData._id,
      memberId: jsonData.memberId,
      rinkId: jsonData.rinkId,
      bookingTypeId: jsonData.bookingTypeId,
      date: new Date(jsonData.date),
      start: new Date(jsonData.start),
      end: new Date(jsonData.end),
      status: jsonData.status,
      participants: jsonData.participants || [],
      notes: jsonData.notes || '',
      createdDate: jsonData.createdDate ? new Date(jsonData.createdDate) : new Date(),
      updatedDate: jsonData.updatedDate ? new Date(jsonData.updatedDate) : new Date()
    });
  }
} 